import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import { CircularProgress } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import {
  IInvoiceItem,
  IModalTypes,
  IResponseSingleInvoice,
  ISingleInvoice,
} from '../type/PaymentsTypes';
import { HttpClient } from '../../../rest/utils/http';
import { getStatusLabel } from '../../../Utils/Comibined';
import OfflinePaymentCardInformation from '../../new-member-applications/IndexFile/paymentInformation/offline/OfflinePaymentCardInformation';
import CommonDetailItem, {
  formatDate,
  formatValue,
} from '../../../Components/CommonDetailItem/CommonDetailItem';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: '90%', md: '860px', lg: '960px' },
  maxWidth: '95vw',
  borderRadius: '16px',
  bgcolor: 'background.paper',
  boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.16)',
  p: { xs: 2, sm: 2.5, md: 3 },
  maxHeight: '90vh',
  overflowY: 'auto',
  outline: 'none',
};

const PaymentDetailsModal = ({
  open,
  handleClose,
  inVoiceId,
  user,
}: IModalTypes) => {
  const [singleInvoice, setSingleInvoice] = useState<ISingleInvoice>();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setLoading(true);
    if (inVoiceId) {
      try {
        (async function () {
          const res: IResponseSingleInvoice = await HttpClient.get(
            `/api/atab/member/invoice/${inVoiceId}`,
          );
          if (res.success) {
            setSingleInvoice(res.data);
            setLoading(false);
          } else {
            setLoading(false);
          }
        })();
      } catch (error) {
        setLoading(false);
      }
    }
  }, [inVoiceId]);

  return (
    <div>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box sx={style}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              borderBottom: '1px solid',
              borderColor: 'divider',
              pb: 1.5,
              mb: 2,
            }}
          >
            <Box>
              <Typography
                id='modal-modal-title'
                variant='h4'
                sx={{
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  color: 'text.primary',
                }}
              >
                Invoice Details
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 0.25 }}
              >
                Check invoice items and payment information
              </Typography>
            </Box>
            <IconButton
              onClick={handleClose}
              size='small'
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {singleInvoice?.invoiceItems?.length ? (
                <Box mb={2}>
                  <Typography
                    variant='h6'
                    sx={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: 'text.primary',
                      mb: 1,
                    }}
                  >
                    Invoice Items
                  </Typography>
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '8px',
                      overflow: 'hidden',
                    }}
                  >
                    <Table size='small' aria-label='invoice items table'>
                      <TableHead sx={{ bgcolor: 'action.hover' }}>
                        <TableRow>
                          <TableCell align='left' sx={{ fontWeight: 600, py: 1 }}>
                            Paid for
                          </TableCell>
                          <TableCell align='center' sx={{ fontWeight: 600, py: 1 }}>
                            Year
                          </TableCell>
                          <TableCell align='right' sx={{ fontWeight: 600, py: 1 }}>
                            Amount (BDT)
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {singleInvoice?.invoiceItems?.map(
                          (sItem: IInvoiceItem, index: number) => {
                            return (
                              <TableRow
                                key={index}
                                sx={{
                                  '&:last-child td, &:last-child th': {
                                    border: 0,
                                  },
                                }}
                              >
                                <TableCell align='left' sx={{ py: 0.75 }}>
                                  {sItem.atab_payment_invoice_item_name}
                                </TableCell>
                                <TableCell align='center' sx={{ py: 0.75 }}>
                                  {sItem.atab_payment_invoice_item_year}
                                </TableCell>
                                <TableCell
                                  align='right'
                                  sx={{ fontWeight: 500, py: 0.75 }}
                                >
                                  {Number(
                                    sItem.atab_payment_invoice_item_amount,
                                  ).toLocaleString()}
                                </TableCell>
                              </TableRow>
                            );
                          },
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : null}

              <Box mb={2}>
                <Grid container spacing={2}>
                  {/* Left Column: Billing Details */}
                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        height: '100%',
                        borderRadius: '12px',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography
                        variant='h6'
                        sx={{
                          fontSize: '1rem',
                          fontWeight: 700,
                          mb: 1.25,
                          color: 'text.primary',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          pb: 0.75,
                        }}
                      >
                        Billing Information
                      </Typography>

                      <Grid container rowSpacing={0.5} columnSpacing={1}>
                        <CommonDetailItem
                          xs={12}
                          sm={12}
                          md={12}
                          lg={12}
                          labelWidth={{ xs: '130px', sm: '140px' }}
                          label='Invoice No'
                          value={formatValue(singleInvoice?.atab_payment_invoice_no)}
                        />
                        <CommonDetailItem
                          xs={12}
                          sm={12}
                          md={12}
                          lg={12}
                          labelWidth={{ xs: '130px', sm: '140px' }}
                          label='Issue Date'
                          value={formatDate(
                            singleInvoice?.atab_payment_invoice_issue_date,
                            'Do MMM YYYY',
                          )}
                        />
                        <CommonDetailItem
                          xs={12}
                          sm={12}
                          md={12}
                          lg={12}
                          labelWidth={{ xs: '130px', sm: '140px' }}
                          label='Status'
                          value={getStatusLabel(
                            Number(singleInvoice?.atab_payment_invoice_due) > 0
                              ? 'unpaid'
                              : 'paid',
                          )}
                        />
                        <CommonDetailItem
                          xs={12}
                          sm={12}
                          md={12}
                          lg={12}
                          labelWidth={{ xs: '130px', sm: '140px' }}
                          label='Total Amount'
                          value={
                            <Typography
                              variant='body2'
                              fontWeight={700}
                              color='primary.main'
                            >
                              {Number(
                                singleInvoice?.atab_payment_invoice_total_amount || 0,
                              ).toLocaleString()}{' '}
                              BDT
                            </Typography>
                          }
                        />
                        <CommonDetailItem
                          xs={12}
                          sm={12}
                          md={12}
                          lg={12}
                          labelWidth={{ xs: '130px', sm: '140px' }}
                          label='Due Amount'
                          value={
                            <Typography
                              variant='body2'
                              fontWeight={700}
                              color={
                                Number(singleInvoice?.atab_payment_invoice_due || 0) > 0
                                  ? 'error.main'
                                  : 'success.main'
                              }
                            >
                              {Number(
                                singleInvoice?.atab_payment_invoice_due || 0,
                              ).toLocaleString()}{' '}
                              BDT
                            </Typography>
                          }
                        />
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Right Column: Company & Contact Details */}
                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        height: '100%',
                        borderRadius: '12px',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography
                        variant='h6'
                        sx={{
                          fontSize: '1rem',
                          fontWeight: 700,
                          mb: 1.25,
                          color: 'text.primary',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          pb: 0.75,
                        }}
                      >
                        Company & Contact
                      </Typography>

                      <Grid container rowSpacing={0.5} columnSpacing={1}>
                        <CommonDetailItem
                          xs={12}
                          sm={12}
                          md={12}
                          lg={12}
                          labelWidth={{ xs: '130px', sm: '140px' }}
                          label='Company'
                          value={formatValue(singleInvoice?.user_member_company_name)}
                        />
                        <CommonDetailItem
                          xs={12}
                          sm={12}
                          md={12}
                          lg={12}
                          labelWidth={{ xs: '130px', sm: '140px' }}
                          label='Representative'
                          value={formatValue(
                            singleInvoice?.user_member_representative_name,
                          )}
                        />
                        <CommonDetailItem
                          xs={12}
                          sm={12}
                          md={12}
                          lg={12}
                          labelWidth={{ xs: '130px', sm: '140px' }}
                          label='Email'
                          value={formatValue(singleInvoice?.user_member_email)}
                        />
                        <CommonDetailItem
                          xs={12}
                          sm={12}
                          md={12}
                          lg={12}
                          labelWidth={{ xs: '130px', sm: '140px' }}
                          label='Phone'
                          value={formatValue(singleInvoice?.user_member_phone)}
                        />
                        <CommonDetailItem
                          xs={12}
                          sm={12}
                          md={12}
                          lg={12}
                          labelWidth={{ xs: '130px', sm: '140px' }}
                          label='Full Address'
                          value={formatValue(
                            singleInvoice?.user_member_representative_full_address,
                          )}
                        />
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              {singleInvoice?.paymentInfo && (
                <Box
                  sx={{
                    mt: 2,
                    pt: 2,
                    borderTop: '1px dashed',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant='h6'
                    sx={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: 'text.primary',
                      mb: 1.25,
                    }}
                  >
                    Payment Transaction Details
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      bgcolor: 'action.hover',
                      borderRadius: '12px',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <OfflinePaymentCardInformation
                      data={singleInvoice.paymentInfo}
                    />
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Modal>
    </div>
  );
};

export default PaymentDetailsModal;
